import { Slot } from '../Modules/Slot/Model/Slot';
import { isRankCheckInProgress, isRankCheckTerminal } from './rankCheckStatus';

/** 생성/수정 후 MID·순위 백그라운드 반영 대기 (폴링 3~5초, 최대 1~2분) */
export const RANK_POLL_INTERVAL_MS = 4000;
export const RANK_POLL_MAX_MS = 120_000;
export const RANK_POLL_BATCH_SIZE = 500;

type RankExpectable = {
  main_keyword?: string | null;
  product_url?: string | null;
  compare_url?: string | null;
  mid_single?: string | null;
  mid_compare?: string | null;
  today_rank?: number | null;
  rank_history?: Slot['rank_history'];
  rank_check_status?: Slot['rank_check_status'];
};

function hasMeaningfulValue(val?: string | null): boolean {
  if (val === null || val === undefined) return false;
  const str = String(val).trim();
  return str !== '' && str !== '-';
}

/** 키워드+URL이 있으면 서버가 비동기로 MID/순위를 채울 수 있음 */
export function shouldExpectAsyncRank(slot: RankExpectable): boolean {
  return (
    hasMeaningfulValue(slot.main_keyword) &&
    (hasMeaningfulValue(slot.product_url) || hasMeaningfulValue(slot.compare_url))
  );
}

const RANK_ASYNC_FIELD_KEYS = [
  'main_keyword',
  'product_url',
  'compare_url',
  'mid_single',
  'mid_compare',
] as const;

/** 수정 DTO가 MID·순위 재계산을 유발할 수 있는지 */
export function dtoTriggersAsyncRank(
  dto: Partial<RankExpectable>,
  base?: RankExpectable,
): boolean {
  const touches = RANK_ASYNC_FIELD_KEYS.some((key) => dto[key] !== undefined);
  if (!touches) return false;
  return shouldExpectAsyncRank({
    main_keyword: dto.main_keyword !== undefined ? dto.main_keyword : base?.main_keyword,
    product_url: dto.product_url !== undefined ? dto.product_url : base?.product_url,
    compare_url: dto.compare_url !== undefined ? dto.compare_url : base?.compare_url,
  });
}

function getTodayInKst(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Seoul',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

/** 오늘자 rank_history가 생기거나 rank_check_status가 종료면 계산 완료 */
export function hasCompletedRankCheck(slot: RankExpectable): boolean {
  if (isRankCheckTerminal(slot.rank_check_status)) return true;

  const history = slot.rank_history;
  const today = getTodayInKst();

  if (Array.isArray(history)) {
    return history.some((entry) => {
      if (!entry || typeof entry !== 'object') return false;
      const item = entry as { date?: unknown; checked_at?: unknown };
      const date = typeof item.date === 'string'
        ? item.date
        : typeof item.checked_at === 'string'
          ? item.checked_at.slice(0, 10)
          : '';
      return date === today;
    });
  }

  return !!history
    && typeof history === 'object'
    && Object.prototype.hasOwnProperty.call(history, today);
}

/** 오늘 순위 계산 기록이 아직 없어 비동기 계산을 기다릴 만한 슬롯인지 */
export function shouldKeepRankPending(slot: RankExpectable): boolean {
  if (isRankCheckInProgress(slot.rank_check_status)) return true;
  if (isRankCheckTerminal(slot.rank_check_status) || slot.rank_check_status === 'idle') {
    return false;
  }
  return shouldExpectAsyncRank(slot) && !hasCompletedRankCheck(slot);
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    const timer = window.setTimeout(resolve, ms);
    const onAbort = () => {
      window.clearTimeout(timer);
      reject(new DOMException('Aborted', 'AbortError'));
    };
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

export type RankPollFetch = (slotIds: number[]) => Promise<{ slots: Slot[]; missing_ids?: number[] }>;

/**
 * batch_detail 폴링으로 today_rank / mid_* 갱신.
 * 오늘자 rank_history가 생기면 대기 종료합니다.
 * rank가 null인 기록도 계산 완료(480위 밖/미발견)로 취급합니다.
 */
export async function pollSlotRanks(
  fetchBatchDetail: RankPollFetch,
  slotIds: number[],
  options: {
    intervalMs?: number;
    maxMs?: number;
    signal?: AbortSignal;
    onUpdate: (slots: Slot[]) => void;
    onFinish: (unclearedIds: number[]) => void;
  },
): Promise<void> {
  const uniqueIds = Array.from(new Set(slotIds.filter((id) => id > 0)));
  if (uniqueIds.length === 0) {
    options.onFinish([]);
    return;
  }

  const intervalMs = options.intervalMs ?? RANK_POLL_INTERVAL_MS;
  const maxMs = options.maxMs ?? RANK_POLL_MAX_MS;
  const pending = new Set(uniqueIds);
  const startedAt = Date.now();

  try {
    while (pending.size > 0 && Date.now() - startedAt < maxMs) {
      await sleep(intervalMs, options.signal);
      if (options.signal?.aborted) return;

      const ids = Array.from(pending);
      const chunks: number[][] = [];
      for (let i = 0; i < ids.length; i += RANK_POLL_BATCH_SIZE) {
        chunks.push(ids.slice(i, i + RANK_POLL_BATCH_SIZE));
      }

      const fetched: Slot[] = [];
      for (const chunk of chunks) {
        if (options.signal?.aborted) return;
        const res = await fetchBatchDetail(chunk);
        fetched.push(...(res.slots ?? []));
      }

      if (fetched.length > 0) {
        options.onUpdate(fetched);
      }

      for (const slot of fetched) {
        if (hasCompletedRankCheck(slot)) {
          pending.delete(slot.slotId);
        }
      }
    }
  } catch (err) {
    if ((err as DOMException)?.name === 'AbortError') return;
    throw err;
  } finally {
    options.onFinish(Array.from(pending));
  }
}
